import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  InputLabel,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { phoneNumberValidation } from "models/constants";
import { states } from "models/states";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormSelectField } from "UI/FormSelectField";
import { FormTextField } from "UI/FormTextField";
import { required } from "utils";
import { customColors } from "models/customColors";

const maxWidthSx = { maxWidth: "600px" };

export const NewPracticeForm = ({
  originator,
  control,
  watch,
  loading,
  setError,
  setValue,
  setProviders,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isPracticeExists, setIsPracticeExists] = useState(false);
  const [practices, setPractices] = useState(null);

  const phoneNumber = watch("practicePhone")?.replace(/_/g, "");

  const isDisabled = !!originator && loading;
  const isPracticePhoneNotEntered =
    isDisabled || !(phoneNumber?.length === 12) || isPracticeExists;

  const getPracticeByPhone = async (phone) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/organizations/find/phone/${phone}`,
        {
          method: "GET",
        }
      );
      const result = await response.json();

      if (result?.length === 0) {
        setValue("practiceId", "", { shouldDirty: true });
        setValue("practiceName", "", { shouldDirty: true });
        setValue("providerId", "", { shouldDirty: true });
        setValue("street", "", { shouldDirty: true });
        setValue("street2", "", { shouldDirty: true });
        setValue("city", "", { shouldDirty: true });
        setValue("state", "", { shouldDirty: true });
        setValue("zipCode", "", { shouldDirty: true });
        setIsPracticeExists(false);
        setProviders([]);
        setPractices(null);
        return;
      }

      if (result?.length === 1) {
        setValue("practiceId", result[0]?.id, { shouldDirty: true });
        setValue("practiceName", result[0]?.name, { shouldDirty: true });
        setValue("street", result[0]?.street, { shouldDirty: true });
        setValue("street2", result[0]?.street2, { shouldDirty: true });
        setValue("city", result[0]?.city, { shouldDirty: true });
        setValue("state", result[0]?.state, { shouldDirty: true });
        setValue("zipCode", result[0]?.zipCode, { shouldDirty: true });
        setIsPracticeExists(true);
        setProviders(result[0]?.providers ?? []);
        setPractices(null);
        return;
      }

      setPractices(result);
    } catch (error) {
      console.error(error);
      setError(error?.message ?? error?.error ?? "Something went wrong");
    }
  };

  useEffect(() => {
    if (phoneNumber?.length === 12) {
      getPracticeByPhone(phoneNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  return (
    <Stack flex={0} gap={1}>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        gap={2}
      >
        <Stack alignItems={"flex-start"}>
          <InputLabel htmlFor="practicePhone">Your Practice Phone</InputLabel>
          <FormMaskedTextField
            id="practicePhone"
            name="practicePhone"
            control={control}
            rules={phoneNumberValidation}
            mask="999-999-9999"
            InputProps={{
              autoComplete: "off",
              disabled: isDisabled,
            }}
          />
        </Stack>
        <Stack flex={1} alignItems={"flex-start"}>
          <InputLabel htmlFor="practiceName">Practice Name</InputLabel>
          <FormTextField
            id="practiceName"
            name="practiceName"
            control={control}
            rules={{ required }}
            InputProps={{
              autoComplete: "off",
              disabled: isPracticePhoneNotEntered,
            }}
          />
        </Stack>
      </Stack>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        gap={2}
      >
        <Stack flex={1} alignItems={"flex-start"}>
          <InputLabel htmlFor="street">Address Line 1</InputLabel>
          <FormTextField
            id="street"
            name="street"
            control={control}
            rules={{ required }}
            InputProps={{
              autoComplete: "off",
              disabled: isPracticePhoneNotEntered,
            }}
          />
        </Stack>
        <Stack alignItems={"flex-start"}>
          <InputLabel htmlFor="street22">Address Line 2 (optional)</InputLabel>
          <FormTextField
            id="stree2"
            name="street2"
            control={control}
            InputProps={{
              autoComplete: "off",
              disabled: isPracticePhoneNotEntered,
            }}
          />
        </Stack>
      </Stack>
      <Stack direction={"row"} gap={2}>
        <Stack alignItems={"flex-start"}>
          <InputLabel htmlFor="city">City</InputLabel>
          <FormTextField
            id="city"
            name="city"
            control={control}
            rules={{ required }}
            InputProps={{
              autoComplete: "off",
              disabled: isPracticePhoneNotEntered,
            }}
          />
        </Stack>
        <Stack alignItems={"flex-start"} minWidth={"30%"} maxWidth={"35%"}>
          <InputLabel htmlFor="state">State</InputLabel>
          <FormSelectField
            id="state"
            name="state"
            control={control}
            options={states}
            rules={{ required }}
            withNone={false}
            InputProps={{
              disabled: isPracticePhoneNotEntered,
            }}
          />
        </Stack>
        <Stack alignItems={"flex-start"}>
          <InputLabel htmlFor="zipCode">Zip Code</InputLabel>
          <FormTextField
            id="zipCode"
            name="zipCode"
            control={control}
            rules={{ required }}
            InputProps={{
              autoComplete: "off",
              disabled: isPracticePhoneNotEntered,
            }}
          />
        </Stack>
      </Stack>
      {practices?.length > 0 && (
        <Dialog open>
          <DialogTitle sx={{ ...maxWidthSx, fontWeight: "bold" }}>
            Choose your Practice
          </DialogTitle>
          <DialogContent sx={{ ...maxWidthSx }}>
            <div className="text-gray-600 mb-6">
              The phone number{" "}
              <span className="text-black font-bold">
                {watch("practicePhone")}
              </span>{" "}
              is linked to multiple practices. Please select your
              practice:
            </div>
            <Divider />
            {practices.map(
              ({
                id,
                name,
                phone,
                speciality,
                street,
                street2,
                state,
                city,
                zipCode,
                providers,
              }) => (
                <React.Fragment key={id}>
                  <Box
                    sx={{
                      textAlign: "left",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: customColors.selector },
                    }}
                    padding={1}
                    key={id}
                    onClick={() => {
                      setValue("practiceId", id, { shouldDirty: true });
                      setValue("practiceName", name, { shouldDirty: true });
                      setValue("street", street, { shouldDirty: true });
                      setValue("street2", street2, { shouldDirty: true });
                      setValue("city", city, { shouldDirty: true });
                      setValue("state", state, { shouldDirty: true });
                      setValue("zipCode", zipCode, { shouldDirty: true });
                      setIsPracticeExists(true);
                      setProviders(providers ?? []);
                      setPractices(null);
                    }}
                  >
                    <span className="text-black">{name}</span>,{" "}
                    {[phone, speciality].filter(Boolean).join(", ")}
                  </Box>
                  <Divider />
                </React.Fragment>
              )
            )}
          </DialogContent>
          <Stack justifyContent={"center"} alignItems={"center"} gap={1} mt={2} mb={4}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setPractices(null);
                setValue("practicePhone", "", { shouldDirty: true });
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Dialog>
      )}
    </Stack>
  );
};
